import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

const messages = {
  ru: {
    navigation: "Навигация",
    loading: "Загрузка данных…",
    storageError: "Не удалось сохранить данные на этом устройстве.",
    actionUnavailable: "Это действие сейчас недоступно.",
    imported: "Персонаж импортирован.",
    importError: "Не удалось импортировать персонажа.",
    reset: "Черновик сброшен.",
    offline: "OFFLINE-FIRST • ДАННЫЕ ОСТАЮТСЯ НА ЭТОМ УСТРОЙСТВЕ",
    name: "Имя",
    level: "Уровень",
    origin: "Происхождение",
    health: "ОЗ",
    gender: "Пол",
    male: "Мужской",
    female: "Женский",
    other: "Другой",
    backstory: "Предыстория",
    portrait: "Портрет",
    noPortrait: "НЕТ ИЗОБРАЖЕНИЯ",
    diceCount: "Количество кубиков",
    roll: "Бросить",
    history: "История бросков",
    tabs: {
      home: "Главная",
      origins: "Происхождение",
      special: "SPECIAL",
      skills: "Навыки",
      perks: "Перки",
      stats: "Сводка",
      dice: "Кубики",
    },
  },
  en: {
    navigation: "Navigation",
    loading: "Loading data…",
    storageError: "Unable to save data on this device.",
    actionUnavailable: "This action is not available.",
    imported: "Character imported.",
    importError: "Unable to import character.",
    reset: "Draft reset.",
    offline: "OFFLINE-FIRST • DATA STAYS ON THIS DEVICE",
    name: "Name",
    level: "Level",
    origin: "Origin",
    health: "HP",
    gender: "Gender",
    male: "Male",
    female: "Female",
    other: "Other",
    backstory: "Backstory",
    portrait: "Portrait",
    noPortrait: "NO IMAGE",
    diceCount: "Dice count",
    roll: "Roll",
    history: "Roll history",
    tabs: {
      home: "Home",
      origins: "Origins",
      special: "SPECIAL",
      skills: "Skills",
      perks: "Perks",
      stats: "Summary",
      dice: "Dice",
    },
  },
} as const;

export type Locale = keyof typeof messages;
export function t(locale: string, key: string): string {
  const value = key
    .split(".")
    .reduce<unknown>(
      (current, part) =>
        current && typeof current === "object"
          ? (current as Record<string, unknown>)[part]
          : undefined,
      messages[locale as Locale] ?? messages.ru,
    );
  return typeof value === "string" ? value : key;
}

const LocaleContext = createContext<{
  locale: Locale;
  setLocale: (locale: Locale) => void;
}>({ locale: "ru", setLocale: () => undefined });
export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocale] = useState<Locale>(() =>
    window.localStorage.getItem("fallout2d20:locale") === "en" ? "en" : "ru",
  );
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);
  const update = (value: Locale) => {
    window.localStorage.setItem("fallout2d20:locale", value);
    setLocale(value);
  };
  return (
    <LocaleContext.Provider value={{ locale, setLocale: update }}>
      {children}
    </LocaleContext.Provider>
  );
}
export const useLocale = () => useContext(LocaleContext);
